require('./viewport.sass');
up.viewport = (function () {
    const u = up.util;
    const e = up.element;
    const f = up.fragment;
    const config = new up.Config(() => ({
        viewportSelectors: ['[up-viewport]', '[up-fixed]'],
        fixedTopSelectors: ['[up-fixed~=top]'],
        fixedBottomSelectors: ['[up-fixed~=bottom]'],
        anchoredRightSelectors: ['[up-anchored~=right]', '[up-fixed~=top]', '[up-fixed~=bottom]', '[up-fixed~=right]'],
        revealSnap: 200,
        revealPadding: 0,
        revealTop: false,
        revealMax() { return 0.5 * window.innerHeight; },
        autoFocusVisible({ element, inputDevice }) { return inputDevice === 'key' || up.form.isField(element); }
    }));
    const bodyShifter = new up.BodyShifter();
    up.compiler(config.selectorFn('anchoredRightSelectors'), function (element) {
        return bodyShifter.onAnchoredElementInserted(element);
    });
    const reveal = up.mockable(function (element, options) {
        var _a, _b;
        options = u.options(options);
        element = f.get(element, options);
        if (!(options.layer = up.layer.get(element))) {
            up.fail('Cannot reveal a detached element');
        }
        if (options.peel) {
            options.layer.peel();
        }
        const motion = new up.RevealMotion(element, options);
        motion.start();
        if (options.focus) {
            tryFocus(element, { force: true, preventScroll: true });
        }
        return ((_b = (_a = up.migrate).formerlyAsync) === null || _b === void 0 ? void 0 : _b.call(_a, 'up.reveal()')) || true;
    });
    function doFocus(element, { preventScroll, force, inputDevice, focusVisible } = {}) {
        if (force) {
            if (!element.hasAttribute('tabindex') && element.tabIndex === -1) {
                element.setAttribute('tabindex', '-1');
            }
        }
        inputDevice !== null && inputDevice !== void 0 ? inputDevice : (inputDevice = up.event.inputDevice);
        focusVisible !== null && focusVisible !== void 0 ? focusVisible : (focusVisible = 'auto');
        focusVisible = u.evalAutoOption(focusVisible, config.autoFocusVisible, { element, inputDevice });
        element.focus({
            preventScroll: true,
            focusVisible,
        });
        removeFocusClasses(element);
        element.classList.add(focusVisible ? 'up-focus-visible' : 'up-focus-hidden');
        if (!preventScroll) {
            return reveal(element);
        }
    }
    function removeFocusClasses(element) {
        element === null || element === void 0 ? void 0 : element.classList.remove('up-focus-hidden', 'up-focus-visible');
    }
    up.on('focusin', function ({ relatedTarget }) {
        removeFocusClasses(relatedTarget);
    });
    function tryFocus(element, options) {
        doFocus(element, options);
        return element === document.activeElement;
    }
    function revealHash(...args) {
        var _a;
        return (_a = revealHashFn(...args)) === null || _a === void 0 ? void 0 : _a();
    }
    function revealHashFn(hash, { strong, layer, origin, behavior = 'instant', focus = false } = {}) {
        if (!hash)
            return;
        let match = firstHashTarget(hash, { layer, origin });
        if (match) {
            return () => {
                let doReveal = () => reveal(match, { top: true, behavior, focus });
                if (strong)
                    u.fastTask(doReveal);
                return doReveal();
            };
        }
        else if (hash === '#top') {
            return () => {
                return scrollTo(0, { behavior });
            };
        }
    }
    function allSelector() {
        return [rootSelector(), ...config.viewportSelectors].join();
    }
    function closest(target, options = {}) {
        const element = f.get(target, options);
        return element.closest(allSelector());
    }
    function getSubtree(element, options = {}) {
        element = f.get(element, options);
        return e.subtree(element, allSelector());
    }
    function getAround(element, options = {}) {
        element = f.get(element, options);
        return e.around(element, allSelector());
    }
    function getAll(options = {}) {
        return f.all(allSelector(), options);
    }
    function rootSelector() {
        return getRoot().tagName;
    }
    function getRoot() {
        return document.scrollingElement;
    }
    function rootWidth() {
        return e.root.clientWidth;
    }
    function rootHeight() {
        return e.root.clientHeight;
    }
    function isRoot(element) {
        return element === getRoot();
    }
    function rootScrollbarWidth() {
        return window.innerWidth - rootWidth();
    }
    function scrollTopKey(viewport) {
        return up.fragment.tryToTarget(viewport);
    }
    function fixedElements(root = document) {
        const queryParts = ['[up-fixed]'].concat(config.fixedTopSelectors).concat(config.fixedBottomSelectors);
        return root.querySelectorAll(queryParts.join());
    }
    function saveScroll(...args) {
        const [viewports, options] = parseOptions(args);
        const location = options.location || options.layer.location;
        if (location) {
            const tops = getScrollTopsForViewportElements(viewports);
            options.layer.lastScrollTops.set(location, tops);
        }
    }
    function getScrollTopsForViewportElements(viewports) {
        let tops = {};
        for (let viewport of viewports) {
            let key = scrollTopKey(viewport);
            if (key) {
                tops[key] = viewport.scrollTop;
            }
            else {
                up.warn('up.viewport.saveScroll()', 'Cannot save scroll positions for untargetable viewport %o', viewport);
            }
        }
        return tops;
    }
    function getScrollTops(...args) {
        let viewports = getAll(...args);
        return getScrollTopsForViewportElements(viewports);
    }
    function restoreScroll(...args) {
        var _a;
        const [viewports, options] = parseOptions(args);
        const scrollTops = (_a = options.scrollTops) !== null && _a !== void 0 ? _a : options.layer.lastScrollTops.get(options.layer.location);
        if (scrollTops) {
            setScrollPositions(viewports, scrollTops, 0);
            up.puts('up.viewport.restoreScroll()', 'Restored scroll positions to %o', scrollTops);
            return true;
        }
        else {
            return false;
        }
    }
    function saveFocus(options = {}) {
        const layer = up.layer.get(options);
        const location = options.location || layer.location;
        if (location) {
            const focusCapsule = up.FocusCapsule.preserve(layer);
            layer.lastFocusCapsules.set(location, focusCapsule);
        }
    }
    function restoreFocus(options = {}) {
        const layer = up.layer.get(options);
        const location = options.location || layer.location;
        const locationCapsule = options.layer.lastFocusCapsules.get(location);
        if (locationCapsule && locationCapsule.restore(layer)) {
            up.puts('up.viewport.restoreFocus()', 'Restored focus to "%s"', locationCapsule.target);
            return true;
        }
        else {
            return false;
        }
    }
    function newStateCache() {
        return new up.FIFOCache({ capacity: 30, normalizeKey: u.matchableURL });
    }
    function parseOptions(args) {
        const [reference, options] = u.args(args, 'val', 'options');
        options.layer = up.layer.get(options);
        let viewports;
        if (reference) {
            viewports = [closest(reference, options)];
        }
        else if (options.around) {
            viewports = getAround(options.around, options);
        }
        else {
            viewports = getAll(options);
        }
        return [viewports, options];
    }
    function scrollTo(position, ...args) {
        const [viewports, options] = parseOptions(args);
        setScrollPositions(viewports, {}, position, options.behavior);
        return true;
    }
    function setScrollPositions(viewports, tops, defaultTop, behavior = 'instant') {
        for (let viewport of viewports) {
            const key = scrollTopKey(viewport);
            let top = tops[key] || defaultTop;
            if (top < 0 || Object.is(top, -0)) {
                top += viewport.scrollHeight - viewport.clientHeight;
            }
            viewport.scrollTo({ top, behavior });
        }
    }
    function absolutize(element, options = {}) {
        var _a;
        const viewport = closest(element);
        const viewportRect = viewport.getBoundingClientRect();
        const originalRect = element.getBoundingClientRect();
        const boundsRect = new up.Rect({
            left: originalRect.left - viewportRect.left,
            top: originalRect.top - viewportRect.top,
            width: originalRect.width,
            height: originalRect.height
        });
        (_a = options.afterMeasure) === null || _a === void 0 ? void 0 : _a.call(options);
        e.setStyle(element, {
            position: element.style.position === 'static' ? 'static' : 'relative',
            top: 'auto',
            right: 'auto',
            bottom: 'auto',
            left: 'auto',
            width: '100%',
            height: '100%'
        });
        const bounds = e.createFromSelector('up-bounds');
        e.insertBefore(element, bounds);
        bounds.appendChild(element);
        const moveBounds = function (diffX, diffY) {
            boundsRect.left += diffX;
            boundsRect.top += diffY;
            return e.setStyle(bounds, boundsRect, 'px');
        };
        moveBounds(0, 0);
        const newElementRect = element.getBoundingClientRect();
        moveBounds(originalRect.left - newElementRect.left, originalRect.top - newElementRect.top);
        u.each(fixedElements(element), e.fixedToAbsolute);
        return {
            bounds,
            moveBounds
        };
    }
    function firstHashTarget(hash, options = {}) {
        hash = pureHash(hash);
        if (hash) {
            const selector = [
                e.idSelector(hash),
                'a' + e.attrSelector('name', hash)
            ].join();
            return f.get(selector, options);
        }
    }
    function pureHash(value) {
        return value === null || value === void 0 ? void 0 : value.replace(/^#/, '');
    }
    function focusedElementWithin(scopeElement) {
        const focusedElement = document.activeElement;
        if (up.fragment.contains(scopeElement, focusedElement)) {
            return focusedElement;
        }
    }
    const CURSOR_PROPS = ['selectionStart', 'selectionEnd', 'scrollLeft', 'scrollTop'];
    function copyCursorProps(from, to = {}) {
        for (let key of CURSOR_PROPS) {
            try {
                to[key] = from[key];
            }
            catch (_error) {
            }
        }
        return to;
    }
    return {
        reveal,
        revealHash,
        revealHashFn,
        firstHashTarget,
        config,
        get: closest,
        subtree: getSubtree,
        around: getAround,
        getScrollTops,
        get root() { return getRoot(); },
        rootWidth,
        rootHeight,
        isRoot,
        rootScrollbarWidth,
        saveScroll,
        restoreScroll,
        scrollTo,
        saveFocus,
        restoreFocus,
        absolutize,
        focus: doFocus,
        tryFocus,
        newStateCache,
        focusedElementWithin,
        copyCursorProps,
        bodyShifter,
    };
})();
up.focus = up.viewport.focus;
up.reveal = up.viewport.reveal;
